import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { fetchAuthFlow, login } from '../actions.js';
import BasicSignIn from './BasicSignIn.js';
import EmailSignIn from './EmailSignIn.js';
import TwoFactorSignIn from './TwoFactorSignIn.js';

function SignInFlow({
  authorizationKey,
  emailDescription,
  initialError,
  onAuthenticated,
  showForgotPassword,
  showTrustDevice,
  title,
}) {
  const [isOtpRequired, setIsOtpRequired] = useState(false);
  const [otpDetails, setOtpDetails] = useState({});
  const [authStep, setAuthStep] = useState('email');
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState(initialError);

  useEffect(() => setError(initialError), [initialError]);

  const setAuthFlow = useCallback((data) => {
    if (data?.flow === 'password') {
      setAuthStep('password');
    } else if (data?.flow === 'saml' && data.sso_url) {
      window.location.href = data.sso_url;
    }
  }, []);

  const handleEmailSubmit = useCallback(
    async (email) => {
      setError('');
      setUserEmail(email);
      await fetchAuthFlow({
        authorizationKey,
        email,
        setError,
        setAuthFlow,
      });
    },
    [authorizationKey, setAuthFlow],
  );

  const handleBackToEmail = useCallback((event) => {
    event.preventDefault();
    setAuthStep('email');
    setUserEmail('');
    setError('');
    setIsOtpRequired(false);
    setOtpDetails({});
  }, []);

  const submit = useCallback(
    (data) =>
      login({
        ...data,
        authorizationKey,
        onSuccess: onAuthenticated,
        setIsOtpRequired,
        setOtpDetails,
      }),
    [authorizationKey, onAuthenticated],
  );

  const { requiredAction } = otpDetails;

  if (authStep === 'email') {
    return (
      <EmailSignIn
        description={emailDescription}
        onEmailSubmit={handleEmailSubmit}
        error={error}
        showForgotPassword={showForgotPassword}
        title={title}
      />
    );
  }

  if (!isOtpRequired || requiredAction) {
    return (
      <BasicSignIn
        submit={submit}
        warning={requiredAction}
        email={userEmail}
        onBackToEmail={handleBackToEmail}
        showForgotPassword={showForgotPassword}
        showTrustDevice={showTrustDevice}
      />
    );
  }

  return <TwoFactorSignIn otpDetails={otpDetails} submit={submit} />;
}

SignInFlow.propTypes = {
  authorizationKey: PropTypes.string,
  emailDescription: PropTypes.string,
  initialError: PropTypes.string,
  onAuthenticated: PropTypes.func,
  showForgotPassword: PropTypes.bool,
  showTrustDevice: PropTypes.bool,
  title: PropTypes.string,
};

SignInFlow.defaultProps = {
  initialError: '',
  showForgotPassword: true,
  showTrustDevice: true,
};

export default SignInFlow;
